import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { AlertCircle, Lock } from 'lucide-react';

moment.locale('es');
const API_URL = process.env.REACT_APP_BACKEND_URL;
const localizer = momentLocalizer(moment);

const PayBadge = ({ estado }) => {
  if (!estado) return null;
  const styles = {
    'Pagado Completo': 'bg-emerald-100 text-emerald-700 border-emerald-300',
    'Abono': 'bg-amber-100 text-amber-700 border-amber-300',
    'Pendiente': 'bg-red-100 text-red-700 border-red-300',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${styles[estado] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
      {estado}
    </span>
  );
};

const messages = {
  today: 'Hoy', previous: '‹', next: '›', month: 'Mes', week: 'Semana', day: 'Día', agenda: 'Agenda',
  date: 'Fecha', time: 'Hora', event: 'Cirugía', noEventsInRange: 'No hay cirugías en este período.',
  showMore: (t) => `+${t} más`,
};

export default function PublicCalendar() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('week');
  const [popover, setPopover] = useState(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: res } = await axios.get(`${API_URL}/api/public/calendar/${token}`);
      setData(res);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Enlace no válido o revocado' : 'Error al cargar el calendario');
    }
    setLoading(false);
  };

  const events = useMemo(() => {
    if (!data) return [];
    const evts = [];
    // Cirugías
    data.surgeries.forEach((s) => {
      if (!s.date || !s.time) return;
      const start = moment(`${s.date} ${s.time}`, 'YYYY-MM-DD HH:mm').toDate();
      const end = s.end_time ? moment(`${s.date} ${s.end_time}`, 'YYYY-MM-DD HH:mm').toDate() : moment(start).add(1, 'hour').toDate();
      // Color: doctor por pabellón > doctor > pabellón > estado
      let color = s.doctor_color || '#2B4C3B';
      if (s.doctor_pavilion_colors && s.doctor_pavilion_colors[s.pavilion]) {
        color = s.doctor_pavilion_colors[s.pavilion];
      } else {
        const pav = data.pavilions.find((p) => p.name === s.pavilion);
        if (pav?.color) color = pav.color;
      }
      evts.push({
        id: s.id,
        title: s.patient_name,
        start,
        end,
        resource: { ...s, color, kind: 'surgery' },
      });
    });
    // Bloqueos
    data.blocks.forEach((b) => {
      if (!b.start_date) return;
      const startDT = b.all_day
        ? moment(b.start_date, 'YYYY-MM-DD').startOf('day').toDate()
        : moment(`${b.start_date} ${b.start_time || '00:00'}`, 'YYYY-MM-DD HH:mm').toDate();
      const endDT = b.all_day
        ? moment(b.end_date || b.start_date, 'YYYY-MM-DD').endOf('day').toDate()
        : moment(`${b.end_date || b.start_date} ${b.end_time || '23:59'}`, 'YYYY-MM-DD HH:mm').toDate();
      evts.push({
        id: 'blk-' + b.id,
        title: b.title || (b.doctor_name ? `${b.doctor_name} — Bloqueo` : 'Bloqueo'),
        start: startDT,
        end: endDT,
        allDay: b.all_day,
        resource: { ...b, color: b.color || '#9B7EBD', kind: 'block' },
      });
    });
    return evts;
  }, [data]);

  const eventPropGetter = (event) => ({
    style: {
      backgroundColor: event.resource?.color || '#2B4C3B',
      borderColor: event.resource?.color || '#2B4C3B',
      color: '#fff',
      borderRadius: 6,
      padding: '2px 4px',
      fontSize: 12,
      fontWeight: 500,
      opacity: event.resource?.kind === 'block' ? 0.85 : 1,
    },
  });

  const handleSelectEvent = (event, e) => {
    setPopover({
      event,
      x: e?.clientX || window.innerWidth / 2,
      y: e?.clientY || 200,
    });
  };

  const closePopover = () => setPopover(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <p className="text-[#6A736B]">Cargando calendario...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] p-6" data-testid="public-calendar-error">
        <div className="max-w-md w-full text-center space-y-3">
          <Lock className="mx-auto text-red-500" size={48} />
          <h1 className="text-2xl font-bold text-[#1A1C18]">{error}</h1>
          <p className="text-[#6A736B]">Solicita un nuevo enlace al administrador de la clínica.</p>
        </div>
      </div>
    );
  }

  const s = popover?.event?.resource;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="public-calendar">
      <header className="bg-[#2B4C3B] text-white px-6 py-4 flex items-center justify-between shadow-sm">
        <div>
          <h1 className="text-xl font-bold">Calendario de Cirugía</h1>
          <p className="text-white/70 text-sm">Vista pública · Solo lectura</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full text-xs">
          <AlertCircle size={14} /> Datos actualizados al momento
        </div>
      </header>

      <div className="p-4 sm:p-6" style={{ height: 'calc(100vh - 80px)' }}>
        <div className="bg-white rounded-xl border border-[#E8E5DD] p-4 h-full flex flex-col public-calendar-wrapper">
          <style>{`
            .public-calendar-wrapper .rbc-toolbar { display: flex !important; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
            .public-calendar-wrapper .rbc-toolbar-label { font-size: 1.125rem; font-weight: 600; color: #1A1C18; }
            .public-calendar-wrapper .rbc-btn-group button { border-color: #E8E5DD; color: #1A1C18; padding: 6px 12px; }
            .public-calendar-wrapper .rbc-btn-group button.rbc-active { background: #2B4C3B; color: #fff; }
            .public-calendar-wrapper .rbc-calendar { flex: 1; min-height: 0; }
          `}</style>
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            view={currentView}
            onView={setCurrentView}
            views={['week', 'month']}
            date={currentDate}
            onNavigate={setCurrentDate}
            eventPropGetter={eventPropGetter}
            onSelectEvent={handleSelectEvent}
            messages={messages}
            popup
            data-testid="public-big-calendar"
          />
        </div>
      </div>

      {popover && s && (
        <>
          <div className="fixed inset-0 z-40" onClick={closePopover} />
          <div
            className="fixed z-50 bg-white border border-[#E8E5DD] rounded-xl shadow-2xl w-[90vw] max-w-sm p-4 space-y-2"
            style={{
              left: Math.min(popover.x, window.innerWidth - 380),
              top: Math.min(popover.y, window.innerHeight - 300),
            }}
            data-testid="public-popover"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-[#1A1C18] break-words">
                  {s.kind === 'block' ? '🚫 ' : ''}{s.patient_name || s.title}
                </h3>
                {s.kind === 'surgery' && s.surgery_type && (
                  <p className="text-sm text-[#6A736B] break-words mt-0.5">{s.surgery_type}</p>
                )}
              </div>
              <button onClick={closePopover} className="text-[#6A736B] hover:text-[#1A1C18]" aria-label="Cerrar">✕</button>
            </div>
            {s.kind === 'surgery' && (
              <div className="space-y-1.5 text-sm">
                <div className="flex gap-2"><span className="text-[#6A736B] w-20 flex-shrink-0">Fecha:</span><span className="font-medium">{moment(popover.event.start).format('dddd D [de] MMMM YYYY')}</span></div>
                <div className="flex gap-2"><span className="text-[#6A736B] w-20 flex-shrink-0">Hora:</span><span className="font-mono font-medium">{s.time} — {s.end_time}</span></div>
                {s.doctor_name && <div className="flex gap-2"><span className="text-[#6A736B] w-20 flex-shrink-0">Doctor:</span><span className="font-medium">{s.doctor_name}</span></div>}
                {s.pavilion && <div className="flex gap-2"><span className="text-[#6A736B] w-20 flex-shrink-0">Pabellón:</span><span>{s.pavilion}</span></div>}
                {s.state_name && (
                  <div className="flex gap-2 items-center">
                    <span className="text-[#6A736B] w-20 flex-shrink-0">Estado:</span>
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold" style={{ backgroundColor: s.state_color + '30', color: s.state_color }}>{s.state_name}</span>
                  </div>
                )}
                {s.atencion_estado_pago && (
                  <div className="flex gap-2 items-center">
                    <span className="text-[#6A736B] w-20 flex-shrink-0">Pago:</span>
                    <PayBadge estado={s.atencion_estado_pago} />
                  </div>
                )}
              </div>
            )}
            {s.kind === 'block' && (
              <div className="space-y-1.5 text-sm">
                <div className="flex gap-2"><span className="text-[#6A736B] w-20 flex-shrink-0">Desde:</span><span>{moment(popover.event.start).format('dddd D [de] MMMM YYYY, HH:mm')}</span></div>
                <div className="flex gap-2"><span className="text-[#6A736B] w-20 flex-shrink-0">Hasta:</span><span>{moment(popover.event.end).format('dddd D [de] MMMM YYYY, HH:mm')}</span></div>
                {s.doctor_name && <div className="flex gap-2"><span className="text-[#6A736B] w-20 flex-shrink-0">Doctor:</span><span>{s.doctor_name}</span></div>}
                {s.pavilion && <div className="flex gap-2"><span className="text-[#6A736B] w-20 flex-shrink-0">Pabellón:</span><span>{s.pavilion}</span></div>}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
